"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { BaseEntity } from "../UniversalCRMTable/types/shared";
import type { GroupedItem, GroupedDataItem } from "@/lib/utils/crm/grouping-strategies";
import { useColumnManager } from "@/lib/column-manager";
import { resolveCardFaceFieldLayout } from "../UniversalCRMTable/utils/resolve-card-face-field-layout";
import { getMobileTypeForField } from "../UniversalCRMTable/fieldRenderers";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { GroupedListViewProps, ListRowRenderContext } from "./types";
import { useGroupingState, useDataGrouping } from "./hooks";
import { GroupHeader, GroupItems } from "./components";
import { EntityListRow } from "./components/EntityListRow";

/**
 * GroupedListView - the Hub "List" view.
 *
 * Renders a dense, single-line-per-record list. With no group-by it's a flat
 * list; with a group-by it's collapsible sections of the same rows. Rows are
 * the generic, config-driven `EntityListRow` (no per-entity code), so every
 * entity gets a real list format distinct from Table (cell grid) and Cards.
 */
export function GroupedListView<T extends BaseEntity>({
  data,
  config,
  groupByField,
  secondaryGroupByField,
  renderItem,
  renderGroupHeader,
  hooks,
  onView,
  onOpenDetailModal,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
}: GroupedListViewProps<T>) {
  // Get visible columns from column manager
  const { visibleColumns } = useColumnManager();

  const visibleColumnIds = React.useMemo(
    () => visibleColumns.filter((c) => c.visible).map((c) => c.id),
    [visibleColumns]
  );

  // Title (primary/secondary) layout — same selection the desktop card uses.
  const fieldLayout = React.useMemo(
    () => resolveCardFaceFieldLayout(config, visibleColumnIds),
    [config, visibleColumnIds]
  );

  // Muted right-cluster fields: status first, then badge- and secondary-typed
  // visible fields, excluding the title fields. Capped so the row never wraps.
  const rightFieldIds = React.useMemo(() => {
    const exclude = new Set(
      [fieldLayout.primaryField, fieldLayout.secondaryField].filter(Boolean) as string[]
    );
    const candidates = visibleColumnIds.filter(
      (id) => config.fields[id] && !config.fields[id]?.formOnly && !exclude.has(id)
    );
    const statusId = config.statusFieldId;
    const statusFirst =
      statusId && !exclude.has(statusId) && config.fields[statusId] ? [statusId] : [];
    const badges = candidates.filter(
      (id) => id !== statusId && getMobileTypeForField(config.fields[id]) === "badge"
    );
    const secondary = candidates.filter(
      (id) => id !== statusId && getMobileTypeForField(config.fields[id]) === "secondary"
    );
    return Array.from(new Set([...statusFirst, ...badges, ...secondary])).slice(0, 4);
  }, [config, visibleColumnIds, fieldLayout]);

  // Stable id list + open handler (referentially stable for the memoized rows).
  const ids = React.useMemo(() => data.map((d) => d.id), [data]);
  const indexOfId = React.useCallback((id: string) => ids.indexOf(id), [ids]);
  const onOpen = React.useCallback(
    (item: T, index: number) => {
      if (onOpenDetailModal) onOpenDetailModal(item, ids, index);
      else onView?.(item);
    },
    [onOpenDetailModal, onView, ids]
  );

  const rowContext = React.useMemo<ListRowRenderContext<T>>(
    () => ({ config, hooks, fieldLayout, rightFieldIds, onOpen, indexOfId, renderItem }),
    [config, hooks, fieldLayout, rightFieldIds, onOpen, indexOfId, renderItem]
  );

  const renderRow = React.useCallback(
    (item: T, index: number) => {
      if (renderItem) return <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>;
      return (
        <EntityListRow<T>
          key={item.id}
          item={item}
          index={index}
          config={config}
          hooks={hooks}
          fieldLayout={fieldLayout}
          rightFieldIds={rightFieldIds}
          onOpen={onOpen}
        />
      );
    },
    [renderItem, config, hooks, fieldLayout, rightFieldIds, onOpen]
  );

  // Group the data
  const { groupedData } = useDataGrouping(data, groupByField, secondaryGroupByField, config);

  // Manage group expansion state
  const { togglePrimaryGroup, toggleSecondaryGroup, isGroupExpanded } = useGroupingState(
    groupByField,
    secondaryGroupByField,
    groupedData
  );

  // Infinite-scroll sentinel (mirrors HorizontalCardView's debounced load-more).
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const lastLoadTimeRef = React.useRef(0);

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (!hasNextPage || isFetchingNextPage) return;
        const now = Date.now();
        if (now - lastLoadTimeRef.current > 500) {
          lastLoadTimeRef.current = now;
          onLoadMore();
        }
      },
      { root, rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);

  const scrollContainerClass =
    "w-full max-h-[min(720px,calc(100vh-15rem))] overflow-y-auto overflow-x-hidden";

  const footer = (
    <>
      <div ref={sentinelRef} aria-hidden className="h-px w-full" />
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      )}
      {!hasNextPage && data.length > 0 && (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          You've reached the end of results
        </div>
      )}
    </>
  );

  if (data.length === 0 && !isFetchingNextPage) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        No items to display
      </div>
    );
  }

  // Flat list (no grouping)
  if (!groupByField) {
    return (
      <div className="space-y-3">
        <div className="border border-border/60 rounded-lg overflow-hidden bg-card">
          <div ref={scrollRef} className={scrollContainerClass}>
            <div className="divide-y divide-border/60">
              {data.map((item, index) => renderRow(item, index))}
            </div>
            {footer}
          </div>
        </div>
      </div>
    );
  }

  // Grouped accordion
  return (
    <div className="space-y-3">
      <div ref={scrollRef} className={scrollContainerClass}>
        <div className="space-y-3">
          {(groupedData as GroupedItem<T>[]).map((item: GroupedItem<T>, index: number) => {
            if (item.type !== "group-header") return null;

            const groupKey = item.groupKey || "";
            const isSecondary = item.level === 2;
            const isPrimary = item.level === 1;
            const parentKey = item.groupKeys?.[0] as string;
            const isExpanded = isGroupExpanded(groupKey, isSecondary ? parentKey : undefined);

            // Collect data items belonging to this group header
            // Only "data" rows are collected here, so the list is narrower than
            // `GroupedItem` — say so, or `GroupItems` infers `BaseEntity`.
            const groupItems: GroupedDataItem<T>[] = [];
            const allGroupedItems = groupedData as GroupedItem<T>[];
            for (let i = index + 1; i < allGroupedItems.length; i++) {
              const nextItem = allGroupedItems[i];
              if (nextItem.type === "data" && nextItem.groupKey === groupKey) {
                groupItems.push(nextItem);
              } else if (nextItem.type === "group-header") {
                break;
              }
            }

            return (
              <div
                key={item.id}
                className={cn(
                  "border rounded-lg overflow-hidden",
                  isPrimary && "border-border/60 bg-card",
                  isSecondary &&
                    "border-blue-200 bg-blue-50/40 ml-4 dark:border-blue-800 dark:bg-blue-950/30"
                )}
              >
                <GroupHeader
                  groupLabel={item.groupLabel || ""}
                  itemCount={item.itemCount || 0}
                  isExpanded={isExpanded}
                  isPrimary={isPrimary}
                  isSecondary={isSecondary}
                  // validate-memo-stable-props-allow: per-group toggle closure; GroupHeader is not memoized and there is one header per group (small N)
                  onToggle={() => {
                    if (isSecondary) {
                      toggleSecondaryGroup(parentKey, groupKey);
                    } else {
                      togglePrimaryGroup(groupKey);
                    }
                  }}
                  renderGroupHeader={renderGroupHeader}
                />

                {isExpanded && (
                  <GroupItems
                    items={groupItems}
                    isPrimary={isPrimary}
                    isSecondary={isSecondary}
                    rowContext={rowContext}
                  />
                )}
              </div>
            );
          })}
        </div>
        {footer}
      </div>
    </div>
  );
}

// Made with Bob
